import { useState, useEffect, useCallback } from 'react';
import { playbooks as playbooksApi } from '../api/client';

function PlaybookFlowcard({ playbook, onEdit, onDelete }) {
  return (
    <div className="pb-flowcard">
      {/* Header */}
      <div className="pb-flowcard-header">
        <div className="pb-flowcard-name">{playbook.name}</div>
        <div className="pb-flowcard-stats">
          <span className="pb-stat">{playbook.trade_count} trades</span>
          <span className="pb-stat">{playbook.win_rate}% WR</span>
          <span className={`pb-stat ${playbook.total_pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
            {playbook.total_pnl >= 0 ? '+' : ''}{playbook.total_pnl}
          </span>
        </div>
      </div>

      {playbook.description && <div className="pb-flowcard-desc">{playbook.description}</div>}

      {/* Flow branches */}
      <div className="pb-flow">
        {playbook.entry_criteria && (
          <div className="pb-flow-step">
            <div className="pb-flow-icon pb-flow-entry">E</div>
            <div className="pb-flow-line" />
            <div className="pb-flow-body">
              <div className="pb-flow-title">Entry Criteria</div>
              <div className="pb-flow-text">{playbook.entry_criteria}</div>
            </div>
          </div>
        )}
        {playbook.risk_rules && (
          <div className="pb-flow-step">
            <div className="pb-flow-icon pb-flow-risk">R</div>
            <div className="pb-flow-line" />
            <div className="pb-flow-body">
              <div className="pb-flow-title">Risk Management</div>
              <div className="pb-flow-text">{playbook.risk_rules}</div>
            </div>
          </div>
        )}
        {playbook.exit_criteria && (
          <div className="pb-flow-step">
            <div className="pb-flow-icon pb-flow-exit">X</div>
            <div className="pb-flow-line" />
            <div className="pb-flow-body">
              <div className="pb-flow-title">Exit Criteria</div>
              <div className="pb-flow-text">{playbook.exit_criteria}</div>
            </div>
          </div>
        )}
        {playbook.rules?.length > 0 && (
          <div className="pb-flow-step">
            <div className="pb-flow-icon pb-flow-check">C</div>
            <div className="pb-flow-line" style={{ display: 'none' }} />
            <div className="pb-flow-body">
              <div className="pb-flow-title">Checklist</div>
              <ul className="pb-flow-rules">
                {playbook.rules.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="pb-flowcard-actions">
        <button className="btn btn-sm" onClick={() => onEdit(playbook)}>Edit</button>
        <button className="btn btn-sm btn-danger" onClick={() => onDelete(playbook.id)}>Delete</button>
      </div>
    </div>
  );
}

export default function PlaybookPage() {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', entry_criteria: '', exit_criteria: '', risk_rules: '', rules: [],
  });
  const [newRule, setNewRule] = useState('');

  const fetchPlaybooks = useCallback(() => {
    playbooksApi.list()
      .then(res => { setPlaybooks(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPlaybooks(); }, [fetchPlaybooks]);

  const resetForm = () => {
    setForm({ name: '', description: '', entry_criteria: '', exit_criteria: '', risk_rules: '', rules: [] });
    setNewRule(''); setEditing(null); setShowForm(false);
  };

  const handleEdit = (pb) => {
    setEditing(pb.id);
    setForm({
      name: pb.name, description: pb.description || '',
      entry_criteria: pb.entry_criteria || '', exit_criteria: pb.exit_criteria || '',
      risk_rules: pb.risk_rules || '', rules: pb.rules || [],
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    const promise = editing ? playbooksApi.update(editing, form) : playbooksApi.create(form);
    promise.then(() => { resetForm(); fetchPlaybooks(); });
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this playbook?')) return;
    playbooksApi.delete(id).then(() => fetchPlaybooks());
  };

  const addRule = () => {
    if (!newRule.trim()) return;
    setForm(prev => ({ ...prev, rules: [...prev.rules, newRule.trim()] }));
    setNewRule('');
  };

  const removeRule = (idx) => {
    setForm(prev => ({ ...prev, rules: prev.rules.filter((_, i) => i !== idx) }));
  };

  if (loading) return <div className="page">Loading playbooks...</div>;

  return (
    <div className="page playbook-page">
      <div className="page-header">
        <h1>Playbooks</h1>
        <button className="btn" onClick={() => { resetForm(); setShowForm(true); }}>New Playbook</button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="pb-form-card">
          <h3>{editing ? 'Edit Playbook' : 'New Playbook'}</h3>
          <div className="pb-form-grid">
            <div className="pb-form-field">
              <label>Name</label>
              <input type="text" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., London Breakout" />
            </div>
            <div className="pb-form-field">
              <label>Description</label>
              <textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your strategy..." rows={2} />
            </div>
            <div className="pb-form-row">
              <div className="pb-form-field">
                <label>Entry Criteria</label>
                <textarea value={form.entry_criteria} onChange={e => setForm(prev => ({ ...prev, entry_criteria: e.target.value }))}
                  placeholder="When to enter..." rows={3} />
              </div>
              <div className="pb-form-field">
                <label>Exit Criteria</label>
                <textarea value={form.exit_criteria} onChange={e => setForm(prev => ({ ...prev, exit_criteria: e.target.value }))}
                  placeholder="When to exit..." rows={3} />
              </div>
            </div>
            <div className="pb-form-field">
              <label>Risk Rules</label>
              <textarea value={form.risk_rules} onChange={e => setForm(prev => ({ ...prev, risk_rules: e.target.value }))}
                placeholder="Position sizing, max risk..." rows={2} />
            </div>
            <div className="pb-form-field">
              <label>Checklist</label>
              <div className="pb-form-rules">
                {form.rules.map((r, i) => (
                  <div key={i} className="pb-form-rule">
                    <span>{r}</span>
                    <button className="btn-icon" onClick={() => removeRule(i)}>&times;</button>
                  </div>
                ))}
                <div className="pb-form-rule-add">
                  <input type="text" value={newRule} onChange={e => setNewRule(e.target.value)}
                    placeholder="Add a rule..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRule(); } }} />
                  <button className="btn btn-sm" onClick={addRule}>+</button>
                </div>
              </div>
            </div>
          </div>
          <div className="pb-form-actions">
            <button className="btn" onClick={handleSave}>{editing ? 'Update' : 'Create'}</button>
            <button className="btn btn-sm" onClick={resetForm}>Cancel</button>
          </div>
        </div>
      )}

      {/* Playbook grid */}
      {playbooks.length === 0 && !showForm ? (
        <div className="empty-state">No playbooks yet. Create your first trading strategy.</div>
      ) : (
        <div className="pb-grid">
          {playbooks.map(pb => (
            <PlaybookFlowcard key={pb.id} playbook={pb} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
